import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiCall } from "utils/apiClient";

const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;
const MEDIA_BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "";
const PUBLIC_SITE_URL = String(process.env.REACT_APP_FRONTEND_URL || "https://seeb.in").replace(
  /\/$/,
  ""
);
const PDF_IMAGE_CACHE = new Map();
const PDF_IMAGE_MIME_BY_EXTENSION = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const toNumber = (value) => {
  const parsed = parseFloat(String(value ?? 0));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) => `Rs ${toNumber(value).toFixed(2)}`;

// ✅ Use REAL backend fields only
const getResolvedRate = (entity) =>
  toNumber(entity?.selling_rate ?? entity?.rate ?? entity?.base_rate);

const getResolvedTotal = (entity) => {
  if (hasValue(entity?.total_amount)) {
    return toNumber(entity.total_amount);
  }

  if (hasValue(entity?.final_amount)) {
    return toNumber(entity.final_amount);
  }

  if (hasValue(entity?.amount)) {
    return toNumber(entity.amount) + toNumber(entity?.cgst_amount) + toNumber(entity?.sgst_amount);
  }

  return getResolvedRate(entity) * toNumber(entity?.quantity);
};

const getItemTotal = (item) => getResolvedTotal(item);

const getItemRate = (item) => getResolvedRate(item);

const getAddonRate = (addon) => getResolvedRate(addon);

const getAddonTotal = (addon) => getResolvedTotal(addon);

const parseImageList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((image) => String(image).trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(Boolean)
          .map((image) => String(image).trim())
          .filter(Boolean);
      }
    } catch {
      return [trimmed];
    }

    return [trimmed];
  }

  return [];
};

const resolveMediaUrl = (path) => {
  if (!path) return "";
  const trimmed = String(path).trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const normalizedPath = trimmed.replace(/^\/+/, "");

  try {
    // Media paths are stored from the backend root (for example public/uploads/...).
    // Using a leading slash prevents an API base path from becoming /api/public/....
    return new URL(`/${normalizedPath}`, MEDIA_BASE_URL).toString();
  } catch {
    const normalizedBase = MEDIA_BASE_URL.replace(/\/$/, "");
    return `${normalizedBase}/${normalizedPath}`;
  }
};

const resolvePdfImageUrl = (path) => {
  const mediaUrl = resolveMediaUrl(path);
  if (!mediaUrl) return "";

  try {
    const parsedUrl = new URL(mediaUrl);
    const filename = parsedUrl.pathname.split("/").filter(Boolean).pop();
    return filename
      ? `${parsedUrl.origin}/quotation-service-image/${encodeURIComponent(filename)}`
      : "";
  } catch {
    return "";
  }
};

const getServiceUrl = (item) => {
  const slug = item?.service_slug ?? item?.slug;
  if (slug) return `${PUBLIC_SITE_URL}/service/${encodeURIComponent(slug)}`;

  const serviceId = item?.service_id ?? item?.serviceId;
  return serviceId
    ? `${PUBLIC_SITE_URL}/service-detail/${encodeURIComponent(serviceId)}`
    : PUBLIC_SITE_URL;
};

const loadImageForPdf = async (url) => {
  if (!url) return null;
  if (PDF_IMAGE_CACHE.has(url)) return PDF_IMAGE_CACHE.get(url);

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;
    const responseType = (response.headers.get("content-type") || "").toLowerCase();
    if (responseType.includes("text/plain")) {
      let imageData = await response.text();
      if (!imageData.startsWith("data:image/")) {
        const extension = url.split("?")[0].split(".").pop()?.toLowerCase();
        const expectedMimeType = PDF_IMAGE_MIME_BY_EXTENSION[extension];
        const base64Separator = imageData.indexOf(";base64,");

        if (expectedMimeType && imageData.startsWith("data:") && base64Separator > 5) {
          imageData = `data:${expectedMimeType}${imageData.slice(base64Separator)}`;
        } else {
          return null;
        }
      }
      if (!imageData.startsWith("data:image/")) {
        return null;
      }
      PDF_IMAGE_CACHE.set(url, imageData);
      return imageData;
    }

    const mimeType = (responseType || "image/png").split(";")[0].trim();
    const imageBuffer = await response.arrayBuffer();
    const blob = new Blob([imageBuffer], { type: mimeType });

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const imageData = String(reader.result || "");
        PDF_IMAGE_CACHE.set(url, imageData);
        resolve(imageData);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const loadPdfImages = async (urls, concurrency = 4) => {
  const uniqueUrls = [...new Set(urls.filter(Boolean))];
  const results = new Map();
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < uniqueUrls.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      const url = uniqueUrls[currentIndex];
      results.set(url, await loadImageForPdf(url));
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, uniqueUrls.length) }, () => worker())
  );
  return results;
};

const sanitizeFileName = (value) =>
  String(value || "customer")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "customer";

const UserCartDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = location.state?.user;

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchUserCartItems = useCallback(async () => {
    if (!user?.user_id) {
      setCartItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await apiCall({
        endpoint: `seeb-cart/getCart/${user.user_id}`,
        method: "GET",
      });

      if (result && result.status === 200) {
        setCartItems(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching user cart details:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    fetchUserCartItems();
  }, [fetchUserCartItems]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/cart");
  };

  // ✅ Grand Total (Trust backend totals)
  const calculatedGrandTotal = cartItems.reduce((sum, item) => {
    const itemTotal = getItemTotal(item);

    const addonsTotal = item.add_ons?.reduce((acc, addon) => acc + getAddonTotal(addon), 0) || 0;

    return sum + itemTotal + addonsTotal;
  }, 0);
  const grandTotal = hasValue(user?.total_amount)
    ? toNumber(user.total_amount)
    : calculatedGrandTotal;

  const getFirstServiceImage = (imageString) => parseImageList(imageString)[0] || null;

  const handleDownloadQuotation = async () => {
    if (!cartItems.length || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      const itemsWithImageUrls = cartItems.map((item) => ({
        ...item,
        pdfImageUrl: resolvePdfImageUrl(getFirstServiceImage(item.service_image)),
      }));
      const loadedImages = await loadPdfImages(itemsWithImageUrls.map((item) => item.pdfImageUrl));
      const preparedItems = itemsWithImageUrls.map((item) => ({
        ...item,
        pdfImage: loadedImages.get(item.pdfImageUrl) || null,
        serviceUrl: getServiceUrl(item),
      }));

      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("SEEB QUOTATION", 14, 19);

      doc.setTextColor(31, 41, 55);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Customer: ${user?.user_name || "-"}`, 14, 40);
      doc.text(`Email: ${user?.user_email || "-"}`, 14, 46);
      doc.text(`Date: ${new Date().toLocaleDateString("en-IN")}`, pageWidth - 14, 40, {
        align: "right",
      });
      doc.text(`Items: ${cartItems.length}`, pageWidth - 14, 46, { align: "right" });

      const rows = [];
      const rowMeta = [];
      preparedItems.forEach((item, itemIndex) => {
        rows.push([
          itemIndex + 1,
          "",
          item.service_name || "Service",
          item.room_name || "-",
          item.room_length && item.room_width
            ? `${item.room_length} x ${item.room_width} ${item.unit || ""}`.trim()
            : "-",
          `${item.quantity || 0} ${item.unit || ""}`.trim(),
          formatCurrency(getItemRate(item)),
          formatCurrency(getItemTotal(item)),
        ]);
        rowMeta.push({ type: "service", item });

        (item.add_ons || []).forEach((addon) => {
          rows.push([
            "",
            "",
            `Add-on: ${addon.addon_name || "-"}`,
            "",
            "-",
            `${addon.quantity || 0} ${item.unit || ""}`.trim(),
            formatCurrency(getAddonRate(addon)),
            formatCurrency(getAddonTotal(addon)),
          ]);
          rowMeta.push({ type: "addon" });
        });
      });

      autoTable(doc, {
        startY: 55,
        head: [["Sr.", "Image", "Service", "Room", "Size", "Qty", "Rate", "Total"]],
        body: rows,
        theme: "grid",
        styles: { fontSize: 8.5, cellPadding: 3, valign: "middle" },
        headStyles: { fillColor: [31, 41, 55], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 20, minCellHeight: 20 },
          2: { cellWidth: 37 },
          3: { cellWidth: 22 },
          4: { cellWidth: 25 },
          5: { cellWidth: 18 },
          6: { cellWidth: 24, halign: "right" },
          7: { cellWidth: 26, halign: "right" },
        },
        didDrawCell: (data) => {
          if (data.section !== "body") return;
          const meta = rowMeta[data.row.index];
          if (meta?.type !== "service") return;

          if (data.column.index === 1 && meta.item.pdfImage) {
            try {
              doc.addImage(
                meta.item.pdfImage,
                data.cell.x + 2,
                data.cell.y + 2,
                16,
                16,
                undefined,
                "FAST"
              );
            } catch (error) {
              console.error("Failed to draw a service image in the quotation:", error);
            }
          }

          if (data.column.index === 2) {
            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
              url: meta.item.serviceUrl,
            });
          }
        },
      });

      let summaryY = (doc.lastAutoTable?.finalY || 60) + 10;
      if (summaryY > doc.internal.pageSize.getHeight() - 18) {
        doc.addPage();
        summaryY = 20;
      }
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`Overall Total: ${formatCurrency(grandTotal)}`, pageWidth - 14, summaryY, {
        align: "right",
      });
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(75, 85, 99);
      doc.text(
        "Click a service name in the quotation to view its details on Seeb.",
        14,
        summaryY + 8
      );

      doc.save(`seeb-quotation-${sanitizeFileName(user?.user_name)}.pdf`);
    } catch (error) {
      console.error("Failed to create cart quotation PDF:", error);
      window.alert("Unable to create the quotation PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="p-6">
        <div className="px-8 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold">Cart Details for {user?.user_name}</h2>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownloadQuotation}
              disabled={loading || !cartItems.length || downloadingPdf}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {downloadingPdf ? "Creating PDF..." : "Download Quotation"}
            </button>
            <button
              type="button"
              onClick={handleBack}
              className="px-3 py-1 text-base font-semibold text-blue-600 hover:text-blue-800"
            >
              ← Back
            </button>
          </div>
        </div>

        <div className="px-8 mt-2 text-gray-600">
          <p>
            <strong>Email:</strong> {user?.user_email}
          </p>
          <p>
            <strong>Total Items:</strong> {cartItems.length}
          </p>
          <p>
            <strong>Total Amount:</strong> {formatCurrency(grandTotal)}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-[75vh] w-full">
            <Spin indicator={antIcon} />
          </div>
        ) : (
          <div className="border-2 mt-6 px-8 pb-6">
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm text-left text-gray-700 border border-gray-300">
                <thead className="text-xs uppercase bg-gray-50">
                  <tr>
                    <th className="border px-6 py-3">Image</th>
                    <th className="border px-6 py-3">Services</th>
                    <th className="border px-6 py-3">Size</th>
                    <th className="border px-6 py-3">Quantity</th>
                    <th className="border px-6 py-3">Rate</th>
                    <th className="border px-6 py-3">Total</th>
                    <th className="border px-6 py-3">Reference Media</th>
                  </tr>
                </thead>

                <tbody>
                  {cartItems.map((item) => {
                    const itemRate = getItemRate(item);
                    const itemTotal = getItemTotal(item);
                    const serviceImage = getFirstServiceImage(item.service_image);
                    const referenceMedia = parseImageList(
                      item.reference_image ?? item.reference_media
                    );

                    const addonsTotal =
                      item.add_ons?.reduce((sum, addon) => sum + getAddonTotal(addon), 0) || 0;

                    return (
                      <React.Fragment key={item.id}>
                        {/* Main Service Row */}
                        <tr className="bg-white">
                          <td className="border px-6 py-4">
                            {serviceImage && (
                              <img
                                src={resolveMediaUrl(serviceImage)}
                                alt="service"
                                className="w-14 h-14 object-cover rounded"
                              />
                            )}
                          </td>

                          <td className="border px-6 py-4">{item.service_name}</td>

                          <td className="border px-6 py-4">
                            {item.room_length && item.room_width
                              ? `${item.room_length} x ${item.room_width} (${item.unit})`
                              : "N/A"}
                          </td>

                          <td className="border px-6 py-4">
                            {item.quantity} {item.unit}
                          </td>

                          <td className="border px-6 py-4 font-medium">
                            {formatCurrency(itemRate)}
                          </td>

                          <td className="border px-6 py-4 font-semibold">
                            {formatCurrency(itemTotal)}
                          </td>

                          <td className="border px-6 py-4">
                            {referenceMedia.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {referenceMedia.map((media, index) => (
                                  <a
                                    key={`${item.id}-ref-${index}`}
                                    href={resolveMediaUrl(media)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <img
                                      src={resolveMediaUrl(media)}
                                      alt={`reference-${index + 1}`}
                                      className="w-14 h-14 object-cover rounded border border-gray-200"
                                    />
                                  </a>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>

                        {/* Add-ons */}
                        {item.add_ons?.map((addon) => (
                          <tr key={addon.id} className="bg-gray-50">
                            <td colSpan={2} className="border px-6 py-2 pl-12">
                              → {addon.addon_name}
                            </td>

                            <td className="border px-6 py-2">
                              {addon.quantity} {item.unit}
                            </td>

                            <td className="border px-6 py-2">
                              {addon.quantity} {item.unit}
                            </td>

                            <td className="border px-6 py-2">
                              {formatCurrency(getAddonRate(addon))}
                            </td>

                            <td className="border px-6 py-2 font-semibold">
                              {formatCurrency(getAddonTotal(addon))}
                            </td>

                            <td className="border px-6 py-2">-</td>
                          </tr>
                        ))}

                        {/* Add-ons Total Row */}
                        {item.add_ons?.length > 0 && (
                          <tr className="bg-gray-100">
                            <td colSpan={5} className="border px-6 py-2 text-right font-semibold">
                              Add-ons Total
                            </td>
                            <td className="border px-6 py-2 font-semibold">
                              {formatCurrency(addonsTotal)}
                            </td>
                            <td className="border" />
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-4 font-bold text-lg">
              Overall Total: {formatCurrency(grandTotal)}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UserCartDetails;
